import type { FilterFn } from "@tanstack/react-table";

/** Sentinel for multi-select filters when the cell is empty (shows "—"). */
export const STRING_MULTI_SELECT_BLANK = "__segment_blank__";

export function stringCellFilterKey(raw: string | null | undefined): string {
    const s = raw?.trim() ?? "";
    return s === "" ? STRING_MULTI_SELECT_BLANK : s;
}

/** Distinct non-empty values (sorted) plus "(Blank)" when any row has no value. */
export function collectStringMultiSelectOptions<TRow>(
    rows: TRow[],
    getRaw: (item: TRow) => string | null | undefined,
): { value: string; label: string }[] {
    const distinct = new Set<string>();
    let hasBlank = false;
    for (const item of rows) {
        const raw = getRaw(item);
        const s = raw?.trim() ?? "";
        if (s === "") {
            hasBlank = true;
        } else {
            distinct.add(s);
        }
    }
    const sorted = [...distinct].sort((a, b) =>
        a.localeCompare(b, undefined, { sensitivity: "base" }),
    );
    const opts: { value: string; label: string }[] = sorted.map((v) => ({
        value: v,
        label: v,
    }));
    if (hasBlank) {
        opts.push({ value: STRING_MULTI_SELECT_BLANK, label: "(Blank)" });
    }
    return opts;
}

/** Empty selection = show all; checked values = OR-match on `getRaw` vs blank sentinel. */
export function createStringMultiSelectFilterFn<TRow>(
    getRaw: (item: TRow) => string | null | undefined,
    _options: { value: string; label: string }[],
): FilterFn<TRow> {
    return (row, _columnId, filterValue) => {
        const selected = filterValue as string[] | undefined;
        if (selected === undefined || selected.length === 0) {
            return true;
        }
        const key = stringCellFilterKey(getRaw(row.original));
        return selected.includes(key);
    };
}
